import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useSemanaAtual, useJogosDaSemana } from '../hooks/useJogos'
import { usePick } from '../hooks/usePick'
import { buscarRecordsTimes } from '../lib/espn'
import { supabase } from '../lib/supabase'

// Formata data para exibição em pt-BR
function formatarHorario(isoString) {
  if (!isoString) return '—'
  const d = new Date(isoString)
  return d.toLocaleString('pt-BR', {
    weekday: 'short', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo'
  })
}

function formatarDeadline(isoString) {
  if (!isoString) return '—'
  const d = new Date(isoString)
  return d.toLocaleString('pt-BR', {
    weekday: 'long', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo'
  })
}

export default function Picks() {
  const { membroAtual, usuario, ligaAtual } = useAuth()
  const { semana, loading: loadingSemana } = useSemanaAtual()
  const { jogos, loading: loadingJogos, erro: erroJogos } = useJogosDaSemana(
    semana?.id,
    semana?.numero,
    2026,
    semana?.tipo === 'regular' ? 2 : 3,
    semana?.spreads_congelados
  )
  const { pick, salvando, erro: erroPick, sucesso, timesUsados, confirmarPick } = usePick(semana?.id)

  const [timeSelecionado, setTimeSelecionado] = useState(null)
  const [spreadSelecionado, setSpreadSelecionado] = useState(null)
  const [jogoSelecionadoId, setJogoSelecionadoId] = useState(null)
  const [records, setRecords] = useState({})
  const [historicoAberto, setHistoricoAberto] = useState(false)
  const [picksHistorico, setPicksHistorico] = useState([])
  const [pickPendente, setPickPendente] = useState(null)
  const [historicoVidas, setHistoricoVidas] = useState([])

  useEffect(() => {
    if (!membroAtual?.id || !ligaAtual?.id) return
    async function carregarHistorico() {
      const { data: picksProcessados } = await supabase
        .from('picks').select('*, semanas(numero, tipo, liga_id)')
        .eq('membro_id', membroAtual.id).not('resultado', 'is', null)
        .order('semana_id', { ascending: true })
      setPicksHistorico((picksProcessados || []).filter(p => p.semanas?.liga_id === ligaAtual.id))

      const { data: pendente } = await supabase
        .from('picks').select('*, semanas(numero, tipo, liga_id, picks_revelados)')
        .eq('membro_id', membroAtual.id).is('resultado', null)
        .not('time_escolhido', 'is', null)
        .order('semana_id', { ascending: false }).limit(1).maybeSingle()
      if (pendente?.semanas?.liga_id === ligaAtual.id) setPickPendente(pendente)

      const { data: hv } = await supabase
        .from('historico_vidas').select('vidas, semana_id, semanas(liga_id)')
        .eq('membro_id', membroAtual.id)
      setHistoricoVidas((hv || []).filter(h => h.semanas?.liga_id === ligaAtual.id))
    }
    carregarHistorico()
  }, [membroAtual?.id, ligaAtual?.id])

  useEffect(() => {
    if (!semana?.numero) return
    buscarRecordsTimes(semana.numero, 2026, semana.tipo === 'regular' ? 2 : 3)
      .then(r => { if (r.sucesso) setRecords(r.records) })
  }, [semana?.numero, semana?.tipo])

  const semanasJogadas = picksHistorico.length
  const spreadMedioRegular = (() => {
    const todos = [
      ...picksHistorico.filter(p => p.semanas?.tipo === 'regular' && p.spread_congelado !== null),
      ...(pickPendente?.semanas?.tipo === 'regular' && pickPendente?.spread_congelado !== null ? [pickPendente] : [])
    ]
    if (!todos.length) return null
    return (todos.reduce((acc, p) => acc + Number(p.spread_congelado), 0) / todos.length).toFixed(1)
  })()
  const vidasMediasHist = (() => {
    if (historicoVidas.length)
      return (historicoVidas.reduce((acc, h) => acc + h.vidas, 0) / historicoVidas.length).toFixed(1)
    return membroAtual?.vidas_restantes != null ? String(membroAtual.vidas_restantes) : null
  })()
  function resultadoLabel(r) {
    if (r === 'win')    return { texto: 'Vitória',  cor: '#1D9E75', bg: '#EAF3DE' }
    if (r === 'loss')   return { texto: 'Derrota',  cor: '#A32D2D', bg: '#FCEBEB' }
    if (r === 'draw')   return { texto: 'Empate',   cor: '#BA7517', bg: '#FAEEDA' }
    if (r === 'nopick') return { texto: 'Sem pick', cor: '#A32D2D', bg: '#FCEBEB' }
    return { texto: '—', cor: '#999', bg: '#f5f5f5' }
  }
  function coracoes(n) { return n > 0 ? '♥'.repeat(n) : '✕' }

  const deadlinePassou = semana?.deadline ? new Date() > new Date(semana.deadline) : false
  const estaEliminado = (membroAtual?.vidas_restantes ?? 1) <= 0
  const pagamentoBloqueado = ligaAtual?.bloquear_sem_pagamento === true && membroAtual?.pagamento_confirmado === false
  const picksAbertos = semana?.picks_abertos && !deadlinePassou && !estaEliminado && !pagamentoBloqueado

  function selecionarTime(jogo, time, spread) {
    if (!picksAbertos) return
    if (timesUsados.includes(time)) return
    setTimeSelecionado(time)
    setSpreadSelecionado(spread)
    setJogoSelecionadoId(jogo.id)
  }

  async function handleConfirmar() {
    if (!timeSelecionado) return
    await confirmarPick(timeSelecionado, jogoSelecionadoId, spreadSelecionado)
    setTimeSelecionado(null)
  }

  if (loadingSemana) return <div style={s.loading}>Carregando semana...</div>
  if (!semana) return (
    <div style={s.banner('#FAEEDA', '#BA7517')}>
      Nenhuma semana ativa no momento. Aguarde o comissário abrir a próxima rodada.
    </div>
  )

  return (
    <div>
      {/* Saudação */}
      <div style={s.greeting}>
        Olá, <strong>{usuario?.primeiro_nome}</strong>
        <span style={s.greetingSub}>Semana {semana.numero} · {semana.tipo === 'regular' ? 'Temporada regular' : 'Playoffs'}</span>
      </div>

      {/* Cards de status */}
      <div style={s.statGrid}>
        <div style={s.statCard}>
          <div style={s.statLabel}>Vidas</div>
          <div style={{ color: '#D85A30', fontSize: 14, letterSpacing: 2 }}>
            {'♥'.repeat(membroAtual?.vidas_restantes || 0) || '✕ eliminado'}
          </div>
          <div style={s.statSub}>{membroAtual?.vidas_restantes} de {membroAtual?.vidas_iniciais}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Pick S{semana.numero}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {pick?.time_escolhido && (
              <img
                src={`/logos/${pick.time_escolhido.toLowerCase()}.png`}
                alt={pick.time_escolhido}
                style={{ width: 22, height: 22, objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none' }}
              />
            )}
            <div style={{ fontSize: 16, fontWeight: 500 }}>{pick?.time_escolhido || '—'}</div>
          </div>
          <div style={s.statSub}>
            {pick?.spread_congelado != null ? `spread: ${pick.spread_congelado}` : 'sem pick'}
          </div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Deadline</div>
          <div style={{ fontSize: 11, fontWeight: 500, color: deadlinePassou ? '#A32D2D' : '#D85A30' }}>
            {deadlinePassou ? 'Encerrado' : 'Aberto'}
          </div>
          <div style={s.statSub}>{formatarDeadline(semana.deadline)}</div>
        </div>
      </div>

      {/* Banner de status */}
      {estaEliminado ? (
        <div style={s.banner('#FCEBEB', '#A32D2D')}>
          ✕ Você foi eliminado e não pode mais fazer picks nesta liga.
        </div>
      ) : pagamentoBloqueado ? (
        <div style={s.banner('#FCEBEB', '#A32D2D')}>
          ⛔ Seus picks estão bloqueados por pagamento pendente. Faça o PIX de <strong>R$100,00</strong> para a chave <strong>{ligaAtual?.chave_pix || '(consulte o comissário)'}</strong> e aguarde a confirmação.
        </div>
      ) : !picksAbertos ? (
        <div style={s.banner('#FCEBEB', '#A32D2D')}>
          🔒 Picks encerrados para esta semana.
        </div>
      ) : (
        <div style={s.banner('#FAEEDA', '#BA7517')}>
          🔒 Spread fixado com os valores do início da rodada · Deadline: {formatarDeadline(semana.deadline)}
        </div>
      )}

      {/* Toast de sucesso */}
      {sucesso && (
        <div style={s.toast}>
          ✓ Pick <strong>{pick?.time_escolhido}</strong> confirmado! Spread fixado: {pick?.spread_congelado}.
        </div>
      )}

      {/* Erro */}
      {(erroPick || erroJogos) && (
        <div style={s.banner('#FCEBEB', '#A32D2D')}>
          {erroPick || erroJogos}
        </div>
      )}

      {/* Times já usados */}
      {timesUsados.length > 0 && (
        <div style={{ marginBottom: '.8rem' }}>
          <div style={s.sectionLabel}>Times já usados esta temporada</div>
          <div style={s.chips}>
            {timesUsados.map(t => (
              <span key={t} style={s.chip}>{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Histórico colapsável */}
      {(picksHistorico.length > 0 || pickPendente) && (
        <div style={{ marginBottom: '1rem' }}>
          <button style={s.histToggle} onClick={() => setHistoricoAberto(a => !a)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500 }}>
              📋 Meu histórico
              <span style={s.histBadge}>{semanasJogadas} semana{semanasJogadas !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {spreadMedioRegular != null && (
                <span style={{ fontSize: 11, color: '#999' }}>Spread méd. {spreadMedioRegular}</span>
              )}
              <span style={{ fontSize: 10, color: '#bbb' }}>{historicoAberto ? '▲' : '▼'}</span>
            </div>
          </button>
          {historicoAberto && (
            <div style={s.histBody}>
              <div style={s.histSumGrid}>
                <div style={s.histSumItem}>
                  <div style={s.histSumVal}>{membroAtual?.vidas_restantes} <span style={{ color: '#D85A30', fontSize: 13 }}>♥</span></div>
                  <div style={s.histSumLabel}>Vidas restantes</div>
                </div>
                <div style={{ ...s.histSumItem, borderLeft: '0.5px solid #eee' }}>
                  <div style={s.histSumVal}>{spreadMedioRegular ?? '—'}</div>
                  <div style={s.histSumLabel}>Spread médio</div>
                </div>
                <div style={{ ...s.histSumItem, borderLeft: '0.5px solid #eee' }}>
                  <div style={s.histSumVal}>{vidasMediasHist ?? '—'}</div>
                  <div style={s.histSumLabel}>Vidas médias</div>
                </div>
              </div>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={s.histTabela}>
                  <thead>
                    <tr>
                      <th style={s.histTh}>Semana</th>
                      <th style={s.histTh}>Time</th>
                      <th style={s.histTh}>Resultado</th>
                      <th style={{ ...s.histTh, textAlign: 'right' }}>Spread 🔒</th>
                      <th style={{ ...s.histTh, textAlign: 'right' }}>Vidas após</th>
                    </tr>
                  </thead>
                  <tbody>
                    {picksHistorico.map(pick => {
                      const r = resultadoLabel(pick.resultado)
                      return (
                        <tr key={pick.id}>
                          <td style={s.histTd}>S{pick.semanas?.numero}</td>
                          <td style={{ ...s.histTd, fontWeight: 500 }}>{pick.time_escolhido || '—'}</td>
                          <td style={s.histTd}>
                            <span style={{ ...s.badge, color: r.cor, background: r.bg }}>{r.texto}</span>
                          </td>
                          <td style={{ ...s.histTd, textAlign: 'right', color: '#888' }}>
                            {pick.spread_congelado !== null ? `🔒 ${pick.spread_congelado}` : '—'}
                          </td>
                          <td style={{ ...s.histTd, textAlign: 'right', color: '#D85A30' }}>
                            {pick.vidas_depois !== null ? coracoes(pick.vidas_depois) : '—'}
                          </td>
                        </tr>
                      )
                    })}
                    {pickPendente && (
                      <tr style={{ opacity: 0.7 }}>
                        <td style={s.histTd}>S{pickPendente.semanas?.numero}</td>
                        <td style={{ ...s.histTd, fontWeight: 500 }}>{pickPendente.time_escolhido}</td>
                        <td style={s.histTd}>
                          <span style={{ ...s.badge, color: '#BA7517', background: '#FAEEDA' }}>Aguardando</span>
                        </td>
                        <td style={{ ...s.histTd, textAlign: 'right', color: '#888' }}>
                          {pickPendente.spread_congelado !== null ? `🔒 ${pickPendente.spread_congelado}` : '—'}
                        </td>
                        <td style={{ ...s.histTd, textAlign: 'right', color: '#bbb' }}>—</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Botão confirmar — TOPO */}
      {picksAbertos && (
        <button
          style={{ ...s.confirmBtn, ...(!timeSelecionado ? s.confirmBtnDisabled : {}), marginBottom: '1rem' }}
          disabled={!timeSelecionado || salvando}
          onClick={handleConfirmar}
        >
          {salvando ? 'Salvando...' : timeSelecionado ? `Confirmar pick: ${timeSelecionado}` : 'Confirmar pick'}
        </button>
      )}

      {/* Lista de jogos */}
      <div style={s.sectionLabel}>Escolha seu time — semana {semana.numero}</div>

      {loadingJogos ? (
        <div style={s.loading}>Carregando jogos...</div>
      ) : jogos.length === 0 ? (
        <div style={s.empty}>Nenhum jogo encontrado para esta semana.</div>
      ) : (
        <div style={s.jogosList}>
          {jogos.map(jogo => {
            const casaUsado = timesUsados.includes(jogo.time_casa)
            const visitanteUsado = timesUsados.includes(jogo.time_visitante)
            const casaSelecionado = timeSelecionado === jogo.time_casa && jogoSelecionadoId === jogo.id
            const visitanteSelecionado = timeSelecionado === jogo.time_visitante && jogoSelecionadoId === jogo.id
            const pickAtualCasa = pick?.time_escolhido === jogo.time_casa
            const pickAtualVisitante = pick?.time_escolhido === jogo.time_visitante

            return (
              <div key={jogo.id} style={s.jogoCard}>
                <div style={s.jogoHeader}>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>
                    {jogo.time_visitante} @ {jogo.time_casa}
                  </span>
                  <span style={{ fontSize: 11, color: '#888' }}>
                    {formatarHorario(jogo.horario)}
                  </span>
                </div>

                <div style={s.teamsRow}>
                  {/* Time visitante */}
                  <button
                    style={{
                      ...s.teamBtn,
                      ...(visitanteUsado ? s.teamBtnUsado : {}),
                      ...(visitanteSelecionado || pickAtualVisitante ? s.teamBtnSel : {}),
                    }}
                    onClick={() => selecionarTime(jogo, jogo.time_visitante, jogo.spread_visitante)}
                    disabled={!picksAbertos || visitanteUsado}
                  >
                    <div style={s.teamAbbrRow}>
                      <img
                        src={`/logos/${jogo.time_visitante.toLowerCase()}.png`}
                        alt={jogo.time_visitante}
                        style={s.teamLogo}
                        onError={e => { e.target.style.display = 'none' }}
                      />
                      <div style={s.teamAbbr}>{jogo.time_visitante}</div>
                    </div>
                    <div style={s.teamName}>{jogo.nome_visitante}</div>
                    {records[jogo.time_visitante] && (
                      <div style={s.recordTag}>({records[jogo.time_visitante]})</div>
                    )}
                    {jogo.spread_visitante != null && (
                      <span style={{
                        ...s.spreadTag,
                        ...(jogo.spread_visitante < 0 ? s.spreadFav : s.spreadDog)
                      }}>
                        {jogo.spread_visitante > 0 ? '+' : ''}{jogo.spread_visitante}
                      </span>
                    )}
                    {visitanteUsado && <span style={s.usadoTag}>já usado</span>}
                    {(visitanteSelecionado || pickAtualVisitante) && !visitanteUsado && (
                      <span style={s.selTag}>✓ selecionado</span>
                    )}
                  </button>

                  <div style={s.vs}>@</div>

                  {/* Time da casa */}
                  <button
                    style={{
                      ...s.teamBtn,
                      ...(casaUsado ? s.teamBtnUsado : {}),
                      ...(casaSelecionado || pickAtualCasa ? s.teamBtnSel : {}),
                    }}
                    onClick={() => selecionarTime(jogo, jogo.time_casa, jogo.spread_casa)}
                    disabled={!picksAbertos || casaUsado}
                  >
                    <div style={s.teamAbbrRow}>
                      <img
                        src={`/logos/${jogo.time_casa.toLowerCase()}.png`}
                        alt={jogo.time_casa}
                        style={s.teamLogo}
                        onError={e => { e.target.style.display = 'none' }}
                      />
                      <div style={s.teamAbbr}>{jogo.time_casa}</div>
                    </div>
                    <div style={s.teamName}>{jogo.nome_casa}</div>
                    {records[jogo.time_casa] && (
                      <div style={s.recordTag}>({records[jogo.time_casa]})</div>
                    )}
                    {jogo.spread_casa != null && (
                      <span style={{
                        ...s.spreadTag,
                        ...(jogo.spread_casa < 0 ? s.spreadFav : s.spreadDog)
                      }}>
                        {jogo.spread_casa > 0 ? '+' : ''}{jogo.spread_casa}
                      </span>
                    )}
                    {casaUsado && <span style={s.usadoTag}>já usado</span>}
                    {(casaSelecionado || pickAtualCasa) && !casaUsado && (
                      <span style={s.selTag}>✓ selecionado</span>
                    )}
                  </button>
                </div>

                {(casaSelecionado || visitanteSelecionado) && (
                  <div style={s.selBar}>
                    {timeSelecionado} selecionado · spread {spreadSelecionado}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Botão confirmar */}
      {picksAbertos && (
        <button
          style={{
            ...s.confirmBtn,
            ...(!timeSelecionado ? s.confirmBtnDisabled : {}),
          }}
          disabled={!timeSelecionado || salvando}
          onClick={handleConfirmar}
        >
          {salvando ? 'Salvando...' : timeSelecionado ? `Confirmar pick: ${timeSelecionado}` : 'Confirmar pick'}
        </button>
      )}
    </div>
  )
}

const s = {
  greeting: { fontSize: 17, fontWeight: 500, marginBottom: '.85rem', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  greetingSub: { color: '#888', fontWeight: 400, fontSize: 13 },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 7, marginBottom: '1rem' },
  statCard: { background: '#f5f5f5', borderRadius: 8, padding: '10px 11px' },
  statLabel: { fontSize: 11, color: '#666', marginBottom: 3 },
  statSub: { fontSize: 10, color: '#999', marginTop: 1 },
  banner: (bg, color) => ({ background: bg, border: `0.5px solid ${color}`, borderRadius: 8, padding: '8px 12px', marginBottom: '.9rem', fontSize: 12, color }),
  toast: { background: '#EAF3DE', border: '0.5px solid #3B6D11', borderRadius: 8, padding: '10px 14px', marginBottom: '.9rem', fontSize: 13, color: '#3B6D11' },
  sectionLabel: { fontSize: 11, fontWeight: 500, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 7 },
  loading: { fontSize: 13, color: '#888', padding: '1.5rem 0', textAlign: 'center' },
  empty: { fontSize: 13, color: '#888', padding: '1.5rem 0', textAlign: 'center' },
  jogosList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: '.8rem' },
  jogoCard: { background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' },
  jogoHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 12px', borderBottom: '0.5px solid #eee', background: '#fafafa' },
  teamsRow: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'stretch' },
  teamBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 6px', cursor: 'pointer', border: 'none', background: 'transparent', gap: 3, minHeight: 74, width: '100%', transition: 'background .12s' },
  teamBtnUsado: { opacity: .38, cursor: 'not-allowed' },
  teamBtnSel: { background: '#FAECE7' },
  teamAbbrRow: { display: 'flex', alignItems: 'center', gap: 5 },
  teamLogo: { width: 28, height: 28, objectFit: 'contain' },
  teamAbbr: { fontSize: 15, fontWeight: 500 },
  teamName: { fontSize: 10, color: '#888' },
  recordTag: { fontSize: 10, color: '#aaa', marginTop: 1 },
  spreadTag: { fontSize: 10, padding: '2px 6px', borderRadius: 99, marginTop: 2 },
  spreadFav: { background: '#FAECE7', color: '#993C1D' },
  spreadDog: { background: '#EAF3DE', color: '#3B6D11' },
  usadoTag: { fontSize: 10, color: '#999' },
  selTag: { fontSize: 10, color: '#D85A30', fontWeight: 500 },
  vs: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', fontSize: 11, color: '#bbb' },
  selBar: { textAlign: 'center', padding: '5px 12px', background: '#FAECE7', fontSize: 11, color: '#993C1D', fontWeight: 500 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  chip: { fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#f5f5f5', border: '0.5px solid #ddd', color: '#666' },
  confirmBtn: { width: '100%', padding: 12, background: '#D85A30', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', minHeight: 48 },
  confirmBtnDisabled: { background: '#eee', color: '#bbb', cursor: 'not-allowed' },
  histToggle: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit', color: '#333', textAlign: 'left' },
  histBadge: { fontSize: 10, background: '#EAF3F8', color: '#1A5C9A', borderRadius: 99, padding: '2px 8px', fontWeight: 500 },
  histBody: { background: 'white', border: '0.5px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 8px 8px' },
  histSumGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderBottom: '0.5px solid #eee' },
  histSumItem: { padding: '10px 12px', textAlign: 'center' },
  histSumVal: { fontSize: 16, fontWeight: 600, color: '#0A2342' },
  histSumLabel: { fontSize: 10, color: '#999', marginTop: 2 },
  histTabela: { minWidth: '100%', borderCollapse: 'collapse', fontSize: 12 },
  histTh: { textAlign: 'left', padding: '6px 12px', borderBottom: '0.5px solid #eee', fontSize: 10, fontWeight: 500, color: '#999', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' },
  histTd: { padding: '8px 12px', borderBottom: '0.5px solid #f5f5f5', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  badge: { fontSize: 11, padding: '2px 7px', borderRadius: 99, fontWeight: 500 },
}
